import { createContext, useRef, useContext, useMemo } from "react";

import PropTypes from "prop-types";

// Below we are defining StableUniqueIdContext that should be pointing to a ref
// pointing to a number. When we need a unique id, we increment the counter.
// This ensures that unique ids do not clash and also that incrementing the
// shared counter does not trigger a re-render. This implementation is also
// consistent between server side rendering and client side rendering, unlike
// for example a global mutable variable.

const StableUniqueIdContext = createContext();

const propTypes = {
  children: PropTypes.node,
};

// StableUniqueIdProvider is an important dependency for useStableUniqueId. Any
// component calling useStableUniqueId should make sure that it's being
// rendered in a context that is wrapped by <StableUniqueIdProvider />. You
// likely also want to only have one <StableUniqueIdProvider /> wrapper in the
// entire react application, unless you want to scope unique ids to particular
// sub-sections of your react render tree.
export const StableUniqueIdProvider = ({ children }) => {
  const stableUniqueId = useRef(0);
  return (
    <StableUniqueIdContext.Provider value={stableUniqueId}>
      {children}
    </StableUniqueIdContext.Provider>
  );
};

StableUniqueIdProvider.propTypes = propTypes;

// useStableUniqueId returns what it says it does. "Stable" here meaning per
// call site, only changing when a depenency does, which is never by default.
const useStableUniqueId = (dependencies = []) => {
  const stableUniqueId = useContext(StableUniqueIdContext);
  const id = useMemo(() => {
    if (!stableUniqueId) {
      throw Error(
        "useStableUniqueId is not configured properly. It should only ever be called from components nested somewhere inside <StableUniqueIdProvider />.",
      );
    }
    return ++stableUniqueId.current;
  }, dependencies);
  return id;
};

export default useStableUniqueId;
